// Sending a specimen-label PDF to the printer.
//
// Scope: specimen labels only. Lab reports and invoices are full-page documents
// that print on the office printer through their own existing paths — nothing
// here touches them. The browser's own print dialog still chooses the printer
// on every job, so this cannot silently route a report to the label printer or
// a sheet of labels to the office printer.
//
// Why an iframe rather than window.open: opening a tab needs the pop-up blocker
// to be off for the site, and every label then costs the user a tab to find,
// print and close. Loading the PDF into a hidden same-origin iframe and calling
// print() on it goes straight to the print dialog in one click, with the tab as
// the fallback when a browser refuses to print an embedded PDF.

// Chrome's PDF plugin attaches slightly after the iframe's load event; asking it
// to print before then silently does nothing.
const PLUGIN_ATTACH_MS = 300;
// Tearing the iframe down cancels an open print dialog, so it outlives the job.
const CLEANUP_MS = 60000;
// If the PDF never loads at all, fall back to a tab rather than leaving the
// button looking like it did nothing.
const LOAD_TIMEOUT_MS = 8000;

/**
 * Print a label PDF blob.
 *
 * @param {Blob} blob - the PDF returned by the LIS label endpoint
 * @returns {Promise<'printed'|'opened'|'blocked'>} 'printed' when the print
 *   dialog was opened, 'opened' when we fell back to a new tab, 'blocked' when
 *   the fallback tab was blocked and the user has to allow pop-ups.
 */
export function printLabelBlob(blob) {
    const url = URL.createObjectURL(blob);
    let iframe = null;

    const cleanup = () => {
        if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
        iframe = null;
        URL.revokeObjectURL(url);
    };

    const openInTab = () => {
        const opened = window.open(url, '_blank');
        // Leave the URL alive long enough for the new tab to load it.
        setTimeout(cleanup, CLEANUP_MS);
        return opened ? 'opened' : 'blocked';
    };

    return new Promise((resolve) => {
        let settled = false;
        const settle = (outcome) => {
            if (settled) return;
            settled = true;
            resolve(outcome);
        };

        try {
            iframe = document.createElement('iframe');
            iframe.setAttribute('aria-hidden', 'true');
            iframe.title = 'Specimen labels';
            // Off-screen rather than display:none — a hidden iframe is not
            // rendered, and an unrendered PDF has nothing to print.
            iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;opacity:0;border:0;';

            iframe.onload = () => {
                setTimeout(() => {
                    // The watchdog may have already fallen back to a tab; don't
                    // then open a second print dialog on top of it.
                    if (settled || !iframe) return;
                    try {
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                        setTimeout(cleanup, CLEANUP_MS);
                        settle('printed');
                    } catch {
                        settle(openInTab());
                    }
                }, PLUGIN_ATTACH_MS);
            };
            iframe.onerror = () => settle(openInTab());

            iframe.src = url;
            document.body.appendChild(iframe);
            setTimeout(() => {
                if (!settled) settle(openInTab());
            }, LOAD_TIMEOUT_MS);
        } catch {
            settle(openInTab());
        }
    });
}

/**
 * Fetch a label PDF from the API and print it.
 *
 * @param {string} url - absolute label endpoint URL
 * @param {Object} opts - { authToken, branchId }
 * @returns {Promise<{ ok: boolean, outcome?: string, message?: string }>}
 */
export async function fetchAndPrintLabels(url, { authToken, branchId } = {}) {
    let res;
    try {
        res = await fetch(url, {
            headers: { Authorization: `Bearer ${authToken}`, 'X-Current-Branch': branchId || '' },
        });
    } catch {
        return { ok: false, message: 'Could not reach the server to load labels' };
    }
    if (!res.ok) {
        return { ok: false, message: res.status === 404 ? 'No specimens to label for this order' : 'Failed to load labels' };
    }
    const outcome = await printLabelBlob(await res.blob());
    if (outcome === 'blocked') {
        return { ok: false, message: 'Allow pop-ups for this site to print labels' };
    }
    return { ok: true, outcome };
}
